import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Film, Lock, Sparkles, ChevronRight, Ticket } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SnacksBar from "../components/SnacksBar";
import { useAuth } from "../lib/auth";

/**
 * /screens — Multiplex Hall page.
 *
 * Renders today's lineup as a grid of cinema-themed screen tiles. Each tile
 * shows a closed-curtain poster, the movie title, and an "Enter" button that
 * routes to /screens/:n. Paid-subscriber gate enforced by the backend.
 */
export default function Screens() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsSub, setNeedsSub] = useState(false);
  const [sp, setSp] = useSearchParams();

  // Handle Stripe redirect for snack orders
  useEffect(() => {
    const sid = sp.get("snack_session_id");
    const cancelled = sp.get("snack_cancel");
    if (sid) {
      // Poll once — webhook usually fires within a few seconds
      const t = setTimeout(async () => {
        try {
          const { data } = await api.get(`/cinema/snacks/order-status/${sid}`);
          if (data.payment_status === "paid") {
            toast.success(`🍿 Order placed — total $${data.total.toFixed(2)}. Check your email for the receipt.`);
          } else {
            toast.info("🍿 Your order is being confirmed — you'll get an email shortly.");
          }
        } catch (e) {/* ignore */}
        sp.delete("snack_session_id"); setSp(sp, { replace: true });
      }, 1500);
      return () => clearTimeout(t);
    }
    if (cancelled) {
      toast.info("Snack order cancelled.");
      sp.delete("snack_cancel"); setSp(sp, { replace: true });
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/screens");
        if (!cancelled) setData(data);
      } catch (e) {
        if (e?.response?.status === 402) setNeedsSub(true);
        else toast.error(e?.response?.data?.detail || "Could not load screens");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="pt-32 px-6 text-gray-400">Loading lineup…</div>
      </div>
    );
  }

  if (needsSub) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <main className="pt-28 px-6 lg:px-12 max-w-3xl mx-auto pb-20">
          <div data-testid="screens-paywall" className="bg-gradient-to-br from-[#1a0a0a] to-[#0a0a0a] border border-[#E50914]/30 rounded-xl p-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#E50914]/15 border border-[#E50914]/40 mb-4">
              <Lock className="w-7 h-7 text-[#E50914]" />
            </div>
            <h1 className="text-3xl font-black mb-2">GCR Theaters Multiplex</h1>
            <p className="text-gray-400 max-w-md mx-auto mb-6">
              The GCR Theaters Multiplex gives you {/* dynamic on backend */}10 parallel screening rooms with hand-picked movies — pick a screen, the curtains rise, and your movie starts. Subscribers only.
            </p>
            <Link to="/pricing" className="btn-red px-6 py-3 rounded font-semibold inline-flex items-center gap-2" data-testid="screens-paywall-cta">
              <Sparkles className="w-4 h-4" /> See plans
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!data?.screens?.length) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white">
        <Navbar />
        <div className="pt-32 px-6 text-gray-400">No movies are available for the multiplex today. Check back soon.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <main className="pt-28 px-6 lg:px-12 max-w-[1500px] mx-auto pb-20">
        {/* Hero */}
        <header className="mb-10">
          <div className="flex items-center gap-2 text-amber-400 mb-3">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs uppercase tracking-[0.28em] font-bold">GCR Theaters Multiplex</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-3">
            Today's Lineup
          </h1>
          <p className="text-gray-400 max-w-2xl text-base sm:text-lg leading-relaxed">
            {data.screen_count} screening rooms, {data.screen_count} different movies. Pick a screen — the curtains rise and your show begins.
          </p>
          <div className="text-[10px] uppercase tracking-widest text-gray-600 mt-3">
            Lineup refreshes daily · {data.date}
          </div>
        </header>

        {/* Screen grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="screens-grid">
          {data.screens.map((s) => (
            <ScreenCard key={s.screen_no} screen={s} onEnter={() => nav(`/screens/${s.screen_no}`)} />
          ))}
        </div>
      </main>
      <SnacksBar />
      <Footer />
    </div>
  );
}

function ScreenCard({ screen, onEnter }) {
  const poster = screen.backdrop || screen.poster;
  return (
    <button
      onClick={onEnter}
      data-testid={`screen-card-${screen.screen_no}`}
      className="group relative text-left bg-gradient-to-br from-[#180c0c] to-[#0a0a0a] rounded-xl border border-white/10 hover:border-[#E50914]/50 overflow-hidden transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#E50914]/10"
    >
      {/* Marquee header (gold trim) */}
      <div className="relative bg-gradient-to-r from-[#1a1308] via-[#241608] to-[#1a1308] border-b border-amber-700/30 px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Film className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-xs uppercase tracking-[0.22em] text-amber-300 font-bold">{screen.screen_label}</span>
        </div>
        <Ticket className="w-3.5 h-3.5 text-amber-400/60" />
      </div>

      {/* Poster + curtain proscenium */}
      <div className="relative aspect-[16/10] overflow-hidden">
        {poster && (
          <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700" />
        )}
        {/* Closed-curtain overlay (cinema vibe) */}
        <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-[#5b0a0a] via-[#7b0d0d]/95 to-transparent transition-transform duration-700 group-hover:-translate-x-full" style={{ boxShadow: "inset -10px 0 30px rgba(0,0,0,0.7)" }}>
          <div className="h-full w-full opacity-30" style={{ backgroundImage: "repeating-linear-gradient(to right, rgba(0,0,0,0.5) 0, rgba(0,0,0,0.5) 2px, transparent 2px, transparent 18px)" }} />
        </div>
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-[#5b0a0a] via-[#7b0d0d]/95 to-transparent transition-transform duration-700 group-hover:translate-x-full" style={{ boxShadow: "inset 10px 0 30px rgba(0,0,0,0.7)" }}>
          <div className="h-full w-full opacity-30" style={{ backgroundImage: "repeating-linear-gradient(to right, rgba(0,0,0,0.5) 0, rgba(0,0,0,0.5) 2px, transparent 2px, transparent 18px)" }} />
        </div>
        {/* Bottom fade */}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black via-black/70 to-transparent" />
      </div>

      {/* Title + meta */}
      <div className="p-4">
        <div className="flex items-start gap-2">
          <h3 className="text-lg font-bold leading-tight line-clamp-1 mb-1 flex-1">{screen.title}</h3>
        </div>
        <div className="text-xs text-gray-500 flex items-center gap-2 mb-3">
          <span>{screen.year || "—"}</span>
          {screen.duration_min && <span>· {screen.duration_min} min</span>}
          {screen.tmdb_rating && <span className="text-amber-400">★ {screen.tmdb_rating.toFixed(1)}</span>}
        </div>
        <p className="text-xs text-gray-400 line-clamp-2 mb-4">{screen.description || ""}</p>
        {screen.is_premiere ? (
          /* Big Pay-to-Watch CTA for ticketed premieres — visible at-a-glance */
          <div className="space-y-1.5">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">
              {screen.premiere_label || "Paid Premiere"}
            </div>
            <div className="flex items-center justify-between bg-[#E50914] text-white rounded-md px-3 py-2 group-hover:bg-[#c40812] transition-colors shadow-lg shadow-[#E50914]/20">
              <span className="text-[11px] uppercase tracking-widest font-bold flex items-center gap-1.5">
                <Ticket className="w-3.5 h-3.5" /> Pay to Watch
              </span>
              <span className="text-base font-black tabular-nums">${screen.ticket_price?.toFixed(2)}</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-widest text-gray-500">Now showing</span>
            <span className="inline-flex items-center gap-1 text-[#E50914] font-semibold text-sm group-hover:gap-2 transition-all">
              Enter <ChevronRight className="w-4 h-4" />
            </span>
          </div>
        )}
      </div>
    </button>
  );
}
